import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faEye, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../utils/comon";

const CommonTable = ({
  headers = [],
  data = [],
  renderCell,
  tableHeading,
  pagination = null,
  onPageChange,
  onLimitChange,
  headerAction,
  onEdit,
  onDelete,
  onPreview,
  customAction = null, // { label: (row) => string, onClick: (row) => void, icon?: any, disabled?: (row) => boolean }
  customActions = [],
  isEditDisabled,
  isDeleteDisabled,
  isPreviewDisabled,
  loading = false,
  hideScrollbar = false,
  expandedRowRender = null, // (row) => ReactNode
  isRowExpandable = null, // (row) => boolean
  renderGroupRow = null, // (row, { isExpanded, toggleGroup }) => ReactNode
  isGroupRow = null, // (row) => boolean
  collapsible = false,
  defaultExpanded = true,
  getGroupId = (row) => row.id || row.tenantId,
  getGroupOfRow = (row) => row.parentId || row.parentTenantId
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [initialExpandedSet, setInitialExpandedSet] = useState(false);

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  useEffect(() => {
    const handleThemeChange = () => {
      setTheme(localStorage.getItem("theme") || "light");
    };
    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);
  const isDark = theme === "dark";

  // Initialize expanded groups on first render
  // useEffect(() => {
  //   if (collapsible && defaultExpanded && !initialExpandedSet && data.length > 0) {
  //     const groups = new Set();
  //     data.forEach(row => {
  //       if (isGroupRow && isGroupRow(row)) {
  //         groups.add(getGroupId(row));
  //       }
  //     });
  //     setExpandedGroups(groups);
  //     setInitialExpandedSet(true);
  //   }
  // }, [data, collapsible, defaultExpanded, isGroupRow, getGroupId, initialExpandedSet]);

  useEffect(() => {
    if (
      collapsible &&
      !initialExpandedSet &&
      data.length > 0
    ) {
      const groupRows = data.filter(
        (row) => isGroupRow && isGroupRow(row)
      );

      const groups = new Set();

      // Existing flow
      if (defaultExpanded) {
        groupRows.forEach((row) => {
          groups.add(getGroupId(row));
        });
      }

      // NEW FLOW:
      // Auto expand when only one group exists
      if (groupRows.length === 1) {
        groups.add(getGroupId(groupRows[0]));
      }

      setExpandedGroups(groups);
      setInitialExpandedSet(true);
    }
  }, [
    data,
    collapsible,
    defaultExpanded,
    isGroupRow,
    getGroupId,
    initialExpandedSet
  ]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set();
      if (!prev.has(groupId)) {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const tableRef = useRef(null);
  const menuRef = useRef(null);

  // Close menu on click outside or scroll
  useEffect(() => {
    const handleClose = (e) => {
      // Don't close if clicking inside the menu
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleClose, true);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleClose, true);
    };
  }, []);

  const handleAction = (e, action, row) => {
    e.stopPropagation();

    // Check if disabled before proceeding
    if (action === 'edit' && isEditDisabled && isEditDisabled(row)) return;
    if (action === 'delete' && isDeleteDisabled && isDeleteDisabled(row)) return;
    if (action === 'preview' && isPreviewDisabled && isPreviewDisabled(row)) return;
    if (action === 'custom' && customAction?.disabled && customAction.disabled(row)) return;

    setOpenMenuId(null);
    if (action === 'edit' && onEdit) onEdit(row);
    if (action === 'delete' && onDelete) onDelete(row);
    if (action === 'preview' && onPreview) onPreview(row);
    if (action === 'custom' && customAction?.onClick) customAction.onClick(row);
  };

  const toggleMenu = (e, rowKey) => {
    e.stopPropagation();
    if (openMenuId === rowKey) {
      setOpenMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate available space
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      const menuHeight = 160; // Estimated height for the menu with 4 items

      // If space below is less than menu height and there's more space above, open upwards
      const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

      setMenuPosition({
        top: shouldOpenUp ? rect.top + scrollY - 8 : rect.bottom + scrollY + 8,
        left: rect.right - 144, // 144 is the width of the menu (w-36)
        isAbove: shouldOpenUp
      });
      setOpenMenuId(rowKey);
    }
  };

  // Close menu if data changes and current row is gone
  useEffect(() => {
    if (openMenuId !== null) {
      const rowExists = data.some((row, idx) => {
        const rowKey = row.uniqueKey || `${row.id || row.key || row.tenantId || row.siteId || row.occupantId || 'row'}-${idx}`;
        return rowKey === openMenuId;
      });
      if (!rowExists) {
        setOpenMenuId(null);
      }
    }
  }, [data, openMenuId]);

  const ActionMenu = ({ row }) => {
    const editDisabled = isEditDisabled && isEditDisabled(row);
    const deleteDisabled = isDeleteDisabled && isDeleteDisabled(row);
    const previewDisabled = isPreviewDisabled && isPreviewDisabled(row);
    const customDisabled = customAction?.disabled && customAction.disabled(row);

    return (
      <div
        ref={menuRef}
        className={`fixed w-52 bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-2xl shadow-xl z-[9999] animate-in fade-in zoom-in duration-200 
          ${menuPosition.isAbove ? "origin-bottom slide-in-from-bottom-2" : "origin-top slide-in-from-top-2"}`}
        style={{
          top: menuPosition.top - window.scrollY,
          left: menuPosition.left - 64, // Adjusted for wider menu
          transform: menuPosition.isAbove ? 'translateY(-100%)' : 'none'
        }}
      >
        <div className="py-2">
          {customActions && customActions.map((action, actIdx) => {
            const isDisabled = action.disabled && action.disabled(row);
            if (action.hide && action.hide(row)) return null;
            return (
              <div key={actIdx} className="relative group w-full">
                <button
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                    if (action.onClick) action.onClick(row);
                  }}
                  className={`w-full flex items-center gap-4 px-5 py-3 transition-colors
                    ${isDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-actionitembg" : "text-[#0F172A] dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg"}
                  `}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    {action.icon && <FontAwesomeIcon icon={action.icon} className={`${isDisabled ? "text-gray-400 dark:text-gray-600" : "text-[#0F172A] dark:text-textprimary"} text-sm`} />}
                  </div>
                  <span className="text-xs font-bold whitespace-nowrap tracking-tight">{typeof action.label === 'function' ? action.label(row) : action.label}</span>
                </button>
              </div>
            );
          })}

          {customAction && (!customAction.hide || !customAction.hide(row)) && (
            <div className="relative group w-full">
              <button
                disabled={customDisabled}
                onClick={(e) => handleAction(e, 'custom', row)}
                className={`w-full flex items-center gap-4 px-5 py-3 transition-colors
                  ${customDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-actionitembg" : "text-[#0F172A] dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg"}
                `}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  {customAction.icon && <FontAwesomeIcon icon={customAction.icon} className={`${customDisabled ? "text-gray-400 dark:text-gray-600" : "text-[#0F172A] dark:text-textprimary"} text-sm`} />}
                </div>
                <span className="text-xs font-bold whitespace-nowrap tracking-tight">{typeof customAction.label === 'function' ? customAction.label(row) : customAction.label}</span>
              </button>
              {customDisabled && (
                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap relative">
                    {customAction.tooltip || "Action disabled"}
                    <div className="w-2 h-2 bg-[#1E293B] rotate-45 absolute -right-1 top-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {onPreview && (
            <div className="relative group w-full">
              <button
                disabled={previewDisabled}
                onClick={(e) => handleAction(e, 'preview', row)}
                className={`w-full flex items-center gap-4 px-5 py-3 transition-colors
                ${previewDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-actionitembg" : "text-[#0F172A] dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg"}
              `}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <FontAwesomeIcon icon={faEye} className={`${previewDisabled ? "text-gray-400 dark:text-gray-600" : "text-[#0F172A] dark:text-textprimary"} text-sm`} />
                </div>
                <span className="text-xs font-bold whitespace-nowrap tracking-tight">Preview</span>
              </button>
              {previewDisabled && (
                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap relative">
                    Action disabled for this record
                    <div className="w-2 h-2 bg-[#1E293B] rotate-45 absolute -right-1 top-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {onEdit && (
            <div className="relative group w-full">
              <button
                disabled={editDisabled}
                onClick={(e) => handleAction(e, 'edit', row)}
                className={`w-full flex items-center gap-4 px-5 py-3 transition-colors
                ${editDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-actionitembg" : "text-[#0F172A] dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg"}
              `}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <FontAwesomeIcon icon={faEdit} className={`${editDisabled ? "text-gray-400 dark:text-gray-600" : "text-[#0F172A] dark:text-textprimary"} text-sm`} />
                </div>
                <span className="text-xs font-bold whitespace-nowrap tracking-tight">Edit</span>
              </button>
              {editDisabled && (
                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap relative">
                    Action disabled for this record
                    <div className="w-2 h-2 bg-[#1E293B] rotate-45 absolute -right-1 top-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {onDelete && (
            <div className="relative group w-full">
              <button
                disabled={deleteDisabled}
                onClick={(e) => handleAction(e, 'delete', row)}
                className={`w-full flex items-center gap-4 px-5 py-3 transition-colors rounded-b-2xl
                ${deleteDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-actionitembg" : "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-700 dark:hover:text-red-400"}
              `}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <FontAwesomeIcon icon={faTrash} className={`${deleteDisabled ? "text-gray-400" : "text-red-500"} text-sm`} />
                </div>
                <span className="text-xs font-bold whitespace-nowrap tracking-tight">Delete</span>
              </button>
              {deleteDisabled && (
                <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                  <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap relative">
                    Action disabled for this record
                    <div className="w-2 h-2 bg-[#1E293B] rotate-45 absolute -right-1 top-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };


  return (
    <div className="border border-gray-200 dark:border-[#363638] bg-white dark:bg-[#1B1B1D] rounded-xl overflow-hidden w-full relative" ref={tableRef}>

      {/* Header */}
      {(tableHeading || headerAction) && (
        <div className={`bg-white dark:bg-[#1B1B1D] p-4 md:p-6 border-b border-gray-100 dark:border-[#363638] flex items-center gap-4 ${tableHeading ? "justify-between" : "justify-end"}`}>
          {tableHeading && (
            <h2 className="text-sm md:text-lg font-bold text-[#0F172A] dark:text-textprimary">
              {tableHeading}
            </h2>
          )}
          {headerAction && <div>{headerAction}</div>}
        </div>
      )}

      <div className={`w-full overflow-x-auto flex-1 ${hideScrollbar ? "scrollbar-hide" : ""}`}>

        <table className="min-w-[800px] w-full text-sm">

          {/* Header */}
          <thead className="bg-[#F8FAFC80] dark:bg-appbg text-[#94A3B8] dark:text-textsecondary tracking-[1.1px] text-xs whitespace-nowrap border-b border-gray-100 dark:border-[#363638]">
            <tr>
              {headers.map((header, index) => {
                const lowerKey = (header.key || '').toLowerCase();
                const isActions = lowerKey === 'actions' || lowerKey === 'action';
                return (
                  <th key={index} className={`px-4 py-3 text-${header.align || "center"}`}>
                    {header.label}
                  </th>
                );
              })}
              {expandedRowRender && (
                <th className="w-10 px-4 py-3"></th>
              )}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {loading ? (
              // Loading Skeleton Rows
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={rowIndex} className="border-t border-gray-100 dark:border-[#363638] bg-white dark:bg-[#1B1B1D] animate-pulse">
                  {headers.map((_, colIndex) => (
                    <td key={colIndex} className="px-4 py-3">
                      <div className="h-4 bg-gray-100 dark:bg-[#2B2B2D] rounded-md w-full" />
                    </td>
                  ))}
                  {expandedRowRender && (
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-100 dark:bg-[#2B2B2D] rounded-md w-full" />
                    </td>
                  )}
                </tr>
              ))
            ) : data.length > 0 ? (
              data.map((row, rowIndex) => {
                // Check if this is a group row
                const isGroup = isGroupRow && isGroupRow(row);
                if (isGroup) {
                  const groupId = getGroupId(row);
                  const isExpanded = expandedGroups.has(groupId);

                  return (
                    <tr key={`group-${rowIndex}`} className="bg-[#F8FAFC40] dark:bg-[#0E0E10] border-t border-gray-100 dark:border-[#363638]">
                      <td colSpan={headers.length + (expandedRowRender ? 1 : 0)} className="px-6 py-3">
                        {renderGroupRow ? renderGroupRow(row, { isExpanded, toggleGroup: () => toggleGroup(groupId) }) : (
                          <div
                            className={`font-bold text-tabletext ${collapsible ? "cursor-pointer flex items-center gap-2" : ""}`}
                            // onClick={collapsible ? () => toggleGroup(groupId) : undefined}
                            onClick={() => toggleGroup(groupId)}
                          >
                            {collapsible && (
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                              />
                            )}
                            {row.groupLabel || "Group"}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                }

                // If collapsible, check if parent group is expanded
                if (collapsible) {
                  const parentGroupId = getGroupOfRow(row);
                  if (parentGroupId && !expandedGroups.has(parentGroupId)) {
                    return null;
                  }
                }

                // Determine a unique ID for the row for menu tracking
                const rowKey = row.uniqueKey || `${row.id || row.key || row.tenantId || row.siteId || row.occupantId || 'row'}-${rowIndex}`;

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`border-t border-gray-100 dark:border-[#363638] bg-white dark:bg-[#1B1B1D] whitespace-nowrap transition-colors ${(expandedRowRender && (!isRowExpandable || isRowExpandable(row))) ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2B2B2D]" : "hover:bg-gray-50/50 dark:hover:bg-[#2B2B2D]/30"}`}
                      onClick={() => {
                        if (expandedRowRender && (!isRowExpandable || isRowExpandable(row))) {
                          setExpandedRows(prev => {
                            if (prev.has(rowKey)) return new Set();
                            return new Set([rowKey]);
                          });
                        }
                      }}
                    >

                      {headers.map((header, colIndex) => {
                        const lowerKey = header.key.toLowerCase();
                        const isActions = lowerKey === 'actions' || lowerKey === 'action';

                        return (
                          <td key={colIndex} className={`px-4 py-3 relative text-${header.align || "center"}`}>

                            {isActions ? (
                              <div className="flex justify-center">
                                {!row.isEmptyPlaceholder && !row.hideActions && (
                                  <button
                                    onClick={(e) => toggleMenu(e, rowKey)}
                                    className="text-gray-400 dark:text-textsecondary hover:text-gray-600 dark:hover:text-textprimary transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2B2B2D]"
                                  >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                  </button>
                                )}

                                {!row.isEmptyPlaceholder && !row.hideActions && openMenuId === rowKey && createPortal(
                                  <ActionMenu row={row} />,
                                  document.body
                                )}
                              </div>
                            ) : (
                              renderCell
                                ? renderCell(header.key, row, header)
                                : (
                                  (typeof row[header.key] === 'string' && row[header.key].length > 20) ? (
                                    <div className="relative group inline-block max-w-[150px] sm:max-w-[200px] align-middle">
                                      <span className="block truncate text-ellipsis overflow-hidden whitespace-nowrap text-tabletext" title="">{row[header.key]}</span>
                                      <div className="absolute left-1/2 bottom-full -translate-x-1/2 mb-2 z-[999] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                                        <div className="bg-[#1E293B] text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap">
                                          {row[header.key]}
                                        </div>
                                        <div className="w-2 h-2 bg-[#1E293B] rotate-45 mx-auto -mt-1"></div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-tabletext">{row[header.key] || "—"}</span>
                                  )
                                )
                            )}

                          </td>
                        );
                      })}

                      {expandedRowRender && (
                        <td className="px-4 py-3 text-center w-10">
                          {(!isRowExpandable || isRowExpandable(row)) && (
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`text-gray-400 dark:text-textsecondary transition-transform duration-300 ${expandedRows.has(rowKey) ? 'rotate-180' : ''}`}
                            />
                          )}
                        </td>
                      )}

                    </tr>

                    {expandedRowRender && (!isRowExpandable || isRowExpandable(row)) && expandedRows.has(rowKey) && (
                      <tr className="bg-[#F8FAFC80] dark:bg-[#0E0E10] border-t border-gray-100 dark:border-[#363638]">
                        <td colSpan={headers.length + 1} className="p-0 text-[#0F172A] dark:text-textprimary">
                          {expandedRowRender(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={headers.length + (expandedRowRender ? 1 : 0)}
                  className="text-center py-20 text-gray-400 dark:text-textsecondary"
                >
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-2xl opacity-20">📭</span>
                    No records found
                  </div>
                </td>
              </tr>
            )}
          </tbody>

        </table>

      </div>

      {/* Pagination Footer */}
      {pagination && (
        <div className="flex flex-col md:flex-row items-center justify-between p-4 px-6 border-t border-gray-100 dark:border-bordercolor gap-4 bg-white dark:bg-cardbg">

          {/* Left Side: Page and Data Count */}
          <div className="text-xs md:text-sm text-textsecondary font-medium whitespace-nowrap">
            Showing <span className="text-textprimary font-bold">
              {(pagination.pageNumber - 1) * pagination.pageSize + 1}
            </span> to <span className="text-textprimary font-bold">
              {Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalRecords)}
            </span> of <span className="text-textprimary font-bold">
              {pagination.totalRecords}
            </span> entries
          </div>

          {/* Right Side: Limit Selector + Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {onLimitChange && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] md:text-xs font-bold text-textsecondary uppercase tracking-wider">Show</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  className="border border-gray-200 dark:border-bordercolor rounded-lg px-2 py-1 bg-white dark:bg-actionitembg font-bold text-[#475569] dark:text-textprimary outline-none text-xs cursor-pointer"
                >
                  {[10, 25, 50, 100].map(limit => (
                    <option key={limit} value={limit}>{limit}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => pagination.pageNumber > 1 && onPageChange(pagination.pageNumber - 1)}
                disabled={pagination.pageNumber === 1}
                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                ${pagination.pageNumber === 1
                    ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                    : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor"
                  }`}
                onMouseEnter={(e) => {
                  if (pagination.pageNumber !== 1) {
                    e.currentTarget.style.borderColor = primaryColor();
                    e.currentTarget.style.color = primaryColor();
                  }
                }}
                onMouseLeave={(e) => {
                  if (pagination.pageNumber !== 1) {
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.color = "";
                  }
                }}
                title="Previous Page"
              >
                Prev
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.ceil(pagination.totalRecords / pagination.pageSize) }, (_, i) => i + 1).map((page) => {
                  const totalPages = Math.ceil(pagination.totalRecords / pagination.pageSize);
                  // Show all if total pages <= 5, otherwise show current and boundaries
                  if (
                    totalPages <= 5 ||
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - pagination.pageNumber) <= 1
                  ) {
                    const isActive = pagination.pageNumber === page;
                    return (
                      <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-all duration-200 border
                        ${isActive
                            ? "text-white"
                            : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border border-gray-200 dark:border-bordercolor"
                          }`}
                        style={isActive ? { backgroundColor: primaryColor(), borderColor: primaryColor() } : {}}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.borderColor = primaryColor();
                            e.currentTarget.style.color = primaryColor();
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.borderColor = "";
                            e.currentTarget.style.color = "";
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    (page === 2 && pagination.pageNumber > 3) ||
                    (page === totalPages - 1 && pagination.pageNumber < totalPages - 2)
                  ) {
                    return <span key={page} className="text-textsecondary px-1 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => pagination.pageNumber < Math.ceil(pagination.totalRecords / pagination.pageSize) && onPageChange(pagination.pageNumber + 1)}
                disabled={pagination.pageNumber === Math.ceil(pagination.totalRecords / pagination.pageSize)}
                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                ${pagination.pageNumber === Math.ceil(pagination.totalRecords / pagination.pageSize)
                    ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                    : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor"
                  }`}
                onMouseEnter={(e) => {
                  if (pagination.pageNumber !== Math.ceil(pagination.totalRecords / pagination.pageSize)) {
                    e.currentTarget.style.borderColor = primaryColor();
                    e.currentTarget.style.color = primaryColor();
                  }
                }}
                onMouseLeave={(e) => {
                  if (pagination.pageNumber !== Math.ceil(pagination.totalRecords / pagination.pageSize)) {
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.color = "";
                  }
                }}
                title="Next Page"
              >
                Next
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonTable;
